import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  Button,
  FormControlLabel,
  Checkbox,
  Typography,
  Link,
  Box,
  IconButton,
  Alert,
  CircularProgress,
} from '@mui/material';
import { useReferral } from '@/contexts/ReferralContext';
import CloseIcon from '@mui/icons-material/Close';
import { useAuth } from '@/contexts/AuthContext';
import { authService } from '@/services/authService';
import { useRateLimit } from '@/hooks/useRateLimit';
import { handleApiError } from '@/services/errorHandler';
import RateLimitDialog from '@/components/common/RateLimitDialog';

interface AuthDialogProps {
  open: boolean;
  onClose: () => void;
  initialMode?: 'login' | 'register';
  mode?: 'login' | 'register';
}

export default function AuthDialog({ open, onClose, initialMode = 'login', mode }: AuthDialogProps) {
  const { login, register, verifyMFA, requestOTP } = useAuth();
  const { referralCode, clearReferralCode } = useReferral();
  const { rateLimitError, handleError, clearRateLimitError, retryAction } = useRateLimit();
  const [currentMode, setCurrentMode] = useState<'login' | 'register'>(mode || initialMode);

  useEffect(() => {
    if (mode) {
      setCurrentMode(mode);
    }
  }, [mode]);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [confirmPasswordError, setConfirmPasswordError] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  
  // MFA States
  const [showMFA, setShowMFA] = useState(false);
  const [mfaMethod, setMfaMethod] = useState<string>();
  const [mfaToken, setMfaToken] = useState('');
  const [tempToken, setTempToken] = useState('');
  const [mfaError, setMfaError] = useState('');

  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email) {
      setEmailError('Email is required');
      return false;
    }
    if (!emailRegex.test(email)) {
      setEmailError('Please enter a valid email address');
      return false;
    }
    setEmailError('');
    return true;
  };

  const validatePassword = (password: string) => {
    if (!password) {
      setPasswordError('Password is required');
      return false;
    }
    if (password.length < 8) {
      setPasswordError('Password must be at least 8 characters long');
      return false;
    }
    setPasswordError('');
    return true;
  };

  const validateConfirmPassword = (password: string, confirmPassword: string) => {
    if (!confirmPassword) {
      setConfirmPasswordError('Please confirm your password');
      return false;
    }
    if (password !== confirmPassword) {
      setConfirmPasswordError('Passwords do not match');
      return false;
    }
    setConfirmPasswordError('');
    return true;
  };

  useEffect(() => {
    if (referralCode) {
      setCurrentMode('register');
    }
  }, [referralCode]);

  const handleGoogleLogin = async () => {
    try {
      setLoading(true);
      setError('');
      const url = await authService.getGoogleAuthUrl(referralCode || undefined);
      window.location.href = url;
    } catch (err: any) {
      setLoading(false);
      if (!handleError(err)) {
        const apiError = handleApiError(err);
        setError(apiError.message);
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const isEmailValid = validateEmail(email);
      const isPasswordValid = validatePassword(password);

      if (currentMode === 'register') {
        const isConfirmPasswordValid = validateConfirmPassword(password, confirmPassword);
        if (!isEmailValid || !isPasswordValid || !isConfirmPasswordValid || !acceptTerms) {
          setLoading(false);
          return;
        }
        await register(email, password, referralCode || undefined);
        if (referralCode) {
          clearReferralCode();
        }
        setCurrentMode('login');
        setError('Registration successful! Please login.');
      } else {
        if (!isEmailValid || !isPasswordValid) {
          setLoading(false);
          return;
        }
        const response = await login(email, password);
        
        if (response.requiresMFA) {
          setShowMFA(true);
          setMfaMethod(response.mfaMethod);
          setTempToken(response.tempToken || '');
          
          if (response.mfaMethod === 'EMAIL_OTP' || response.mfaMethod === 'SMS_OTP') {
            await requestOTP(response.mfaMethod, email, response.tempToken || '');
          }
        } else {
          onClose();
        }
      }
    } catch (err: any) {
      setLoading(false);
      if (!handleError(err)) {
        const apiError = handleApiError(err);
        setError(apiError.message);
      }
    }
  };

  const handleMFASubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setMfaError('');
    setLoading(true);

    try {
      await verifyMFA(mfaToken, tempToken, mfaMethod || '');
      onClose();
    } catch (err: any) {
      setLoading(false);
      if (!handleError(err)) {
        const apiError = handleApiError(err);
        setMfaError(apiError.message);
      }
    }
  };

  const handleRequestNewOTP = async () => {
    setMfaError('');
    setLoading(true);

    try {
      if (mfaMethod === 'EMAIL_OTP' || mfaMethod === 'SMS_OTP') {
        await requestOTP(mfaMethod, email, tempToken);
        setMfaError('New code has been sent');
      }
    } catch (err: any) {
      setLoading(false);
      if (!handleError(err)) {
        const apiError = handleApiError(err);
        setMfaError(apiError.message);
      }
    }
  };

  const toggleMode = () => {
    setCurrentMode(currentMode === 'login' ? 'register' : 'login');
    setEmail('');
    setPassword('');
    setConfirmPassword('');
    setAcceptTerms(false);
    setEmailError('');
    setPasswordError('');
    setConfirmPasswordError('');
    setError('');
    setShowMFA(false);
    setMfaToken('');
    setTempToken('');
    setMfaError('');
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          {showMFA ? 'Two-Factor Authentication' : currentMode === 'register' ? 'Create an Account' : 'Welcome Back'}
          <IconButton
            aria-label="close"
            onClick={onClose}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          {showMFA ? (
            <Box component="form" onSubmit={handleMFASubmit} sx={{ mt: 2 }}>
              <Typography variant="body1" sx={{ mb: 2 }}>
                {mfaMethod === 'TOTP'
                  ? 'Please enter the code from your authenticator app'
                  : `Please enter the code sent to your ${mfaMethod === 'EMAIL_OTP' ? 'email' : 'phone'}`
                }
              </Typography>
              <TextField
                fullWidth
                label="Authentication Code"
                value={mfaToken}
                onChange={(e) => setMfaToken(e.target.value)}
                error={!!mfaError}
                helperText={mfaError}
                margin="normal"
                required
              />
              {(mfaMethod === 'EMAIL_OTP' || mfaMethod === 'SMS_OTP') && (
                <Button
                  onClick={handleRequestNewOTP}
                  disabled={loading}
                  sx={{ mt: 1 }}
                >
                  Request New Code
                </Button>
              )}
              <Button
                type="submit"
                fullWidth
                variant="contained"
                color="primary"
                disabled={!mfaToken || loading}
                sx={{ mt: 3, mb: 2 }}
              >
                {loading ? <CircularProgress size={24} /> : 'Verify'}
              </Button>
            </Box>
          ) : (
            <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
              <Button
                variant="outlined"
                color="primary"
                fullWidth
                onClick={handleGoogleLogin}
                disabled={loading}
                startIcon={<img src="/google.svg" alt="Google" width={20} height={20} />}
                sx={{ mb: 3 }}
              >
                Continue with Google
              </Button>
              
              <TextField
                fullWidth
                label="Email Address"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                error={!!emailError}
                helperText={emailError}
                margin="normal"
                required
              />
              <TextField
                fullWidth
                label="Password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                error={!!passwordError}
                helperText={passwordError}
                margin="normal"
                required
              />
              {currentMode === 'register' && (
                <>
                  <TextField
                    fullWidth
                    label="Confirm Password"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    error={!!confirmPasswordError}
                    helperText={confirmPasswordError}
                    margin="normal"
                    required
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={acceptTerms}
                        onChange={(e) => setAcceptTerms(e.target.checked)}
                        color="primary"
                        required
                      />
                    }
                    label={
                      <Typography variant="body2">
                        I accept the{' '}
                        <Link href="/terms" target="_blank" rel="noopener">
                          Terms & Conditions
                        </Link>{' '}
                        and{' '}
                        <Link href="/privacy" target="_blank" rel="noopener">
                          Privacy Policy
                        </Link>
                      </Typography>
                    }
                    sx={{ mt: 2 }}
                  />
                </>
              )}
              <Button
                type="submit"
                fullWidth
                variant="contained"
                color="primary"
                sx={{ mt: 3, mb: 2 }}
                disabled={
                  loading ||
                  (currentMode === 'register'
                    ? !email || !password || !confirmPassword || !acceptTerms ||
                      !!emailError || !!passwordError || !!confirmPasswordError
                    : !email || !password || !!emailError || !!passwordError)
                }
              >
                {loading ? <CircularProgress size={24} /> : currentMode === 'register' ? 'Register Now' : 'Login'}
              </Button>
              <Typography variant="body2" align="center">
                {currentMode === 'register' ? 'Already have an account?' : "Don't have an account?"}{' '}
                <Link component="button" variant="body2" onClick={toggleMode}>
                  {currentMode === 'register' ? 'Login here' : 'Register here'}
                </Link>
              </Typography>
            </Box>
          )}
        </DialogContent>
      </Dialog>
      
      {/* Rate Limit Dialog */}
      {rateLimitError && (
        <RateLimitDialog
          open={!!rateLimitError}
          onClose={clearRateLimitError}
          error={rateLimitError}
          onRetry={() => {
            // Retry the last action that failed
            if (showMFA) {
              handleMFASubmit(new Event('submit') as any);
            } else {
              handleSubmit(new Event('submit') as any);
            }
          }}
        />
      )}
    </>
  );
}